import React from 'react'
import { FormField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { NameDto } from 'api/Common/Types'
import { CampaignRebateDto } from 'api/Trading-Accounts/Types/MarketingCampaignDetailDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'
import { isNumeric } from 'utils/is-numeric'
import { v4 as uuid } from 'uuid'

type CampaignDetailsBusinessRuleProps = FormikProps<CampaignRebateConfigurationFormValues> &
  OuterProps
export type CampaignRebateConfiguration = CampaignRebateDto & { id: string }

export interface CampaignRebateConfigurationFormValues {
  campaignCalculationTypeId: number | undefined
  rebateValue: string
  minimumVolume: string
  rebateMultiplier: number
}

const CampaignTierMinValue = 1

export const CampaignRebateConfigurationTierModalUI: React.FC<CampaignDetailsBusinessRuleProps> = (
  _props
) => {
  return (
    <Form className='space-y-4'>
      <div className='grid w-full gap-3'>
        <FormField name='rebateValue' label='Tier' type='number' min={CampaignTierMinValue} />
        <FormField name='minimumVolume' label='Minimum Volume' type='number' min={0} />
        <FormField name='rebateMultiplier' label='Multiplier' type='number' step='0.01' />
      </div>

      <div className='flex justify-end'>
        <Button type='submit'>Save</Button>
      </div>
    </Form>
  )
}

interface OuterProps {
  data?: CampaignRebateConfiguration
  rebates: CampaignRebateDto[]
  calculationTypeId: number | undefined
  calculationTypes: NameDto[]
  onSubmit(values?: FormSubmitValues<CampaignRebateConfiguration>): void
}

const dataToSubmit = (
  props: OuterProps,
  values: CampaignRebateConfigurationFormValues
): FormSubmitValues<CampaignRebateConfiguration | undefined> => {
  const { data, calculationTypes } = props

  const calculationType = calculationTypes.find((x) => x.id === values?.campaignCalculationTypeId)

  if (calculationType) {
    return {
      id: data?.id || uuid(),
      campaignCalculationTypeId: calculationType,
      rebateValue: Number.parseInt(values.rebateValue, 10) || 0,
      minimumVolume: Number.parseInt(values.minimumVolume, 10) || 0,
      rebateMultiplier: values.rebateMultiplier || 0,
    }
  }

  return data
}

const initialState = (props: OuterProps) => {
  const { data, calculationTypeId } = props

  return {
    campaignCalculationTypeId: calculationTypeId || undefined,
    rebateValue: data?.rebateValue?.toString() || '',
    minimumVolume: data?.minimumVolume?.toString() || '',
    rebateMultiplier: data?.rebateMultiplier || 0,
  }
}

export const CampaignRebateConfigurationTierModal = withFormik<
  OuterProps,
  CampaignRebateConfigurationFormValues
>({
  mapPropsToValues: (props) => {
    return initialState(props)
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(props, values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, props) => {
    const { data, rebates } = props
    const errors: FormikErrors<CampaignRebateConfigurationFormValues> = {}

    const rebateValue = values.rebateValue ? Number.parseInt(values.rebateValue, 10) : -1

    if (!rebateValue) {
      errors.rebateValue = 'Required'
    }

    if (rebateValue && rebateValue < CampaignTierMinValue) {
      errors.rebateValue = `Tier value must be at least ${CampaignTierMinValue}`
    }

    if (
      values.rebateValue &&
      rebates
        .filter((x) => x.rebateValue !== data?.rebateValue)
        .map((x) => x.rebateValue)
        .includes(rebateValue)
    ) {
      errors.rebateValue = 'This Tier is already in use. Please choose a unique value'
    }

    if (!values.minimumVolume) {
      errors.minimumVolume = 'Required'
    }

    if (!values.rebateMultiplier) {
      errors.rebateMultiplier = 'Required'
    }

    if (values.rebateValue && !isNumeric(values.rebateValue)) {
      errors.rebateValue = 'Tier must be a numeric value. Please enter a number'
    }

    if (values.minimumVolume && !isNumeric(values.minimumVolume)) {
      errors.minimumVolume = 'Minimum volume must be a numeric value. Please enter a number'
    }

    if (values.rebateMultiplier && !isNumeric(values.rebateMultiplier)) {
      errors.rebateMultiplier = 'Rebate multiplier must be a numeric value. Please enter a number'
    }

    return errors
  },
  enableReinitialize: true,
})(CampaignRebateConfigurationTierModalUI)
